import React from 'react';

import { Stack, Typography } from '@mui/material';
import { border } from 'settings/tokens';

// VERA-315: multi-select, pills-as-toggle status filter — an MUI port of the
// platform's `StatusChipFilter` (apps/platform/src/components/input/filters/StatusChipFilter.tsx).
// Click a chip to add/remove it from `selected`; no checkbox. Weight-neutral: constant
// 1px border + fixed padding in BOTH states, so toggling never shifts layout. A selected
// chip renders as a SOLID filled StatusBadge (fill/border/text from the status colors) so
// the filter reads as the same status shown on the cards; unselected is a plain grey outline.

export type StatusPillOption = {
  value: string
  label: string
  /** StatusBadge fill when selected. */
  fillColor: string
  /** StatusBadge border when selected. */
  borderColor: string
  /** StatusBadge text when selected. */
  textColor: string
}

interface StatusPillFilterProps {
  options: StatusPillOption[]
  selected: string[]
  onToggle: (value: string) => void
}

export const StatusPillFilter = ({ options, selected, onToggle }: StatusPillFilterProps) => {
  return (
    /* 8px, not 12. The chips carry a 44px invisible touch overlay that extends
       ~4px past the visible pill top and bottom, so a 12px gap READ as ~20px
       between wrapped rows — the pills looked like separate groups rather than
       one set of options. 8px puts the perceived gap back at roughly the
       intended 12. */
    <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
      {options.map(opt => {
        const isOn = selected.includes(opt.value);
        return (
          <Stack
            key={opt.value}
            direction="row"
            alignItems="center"
            justifyContent="center"
            onClick={() => onToggle(opt.value)}
            sx={{
              // Sized to its text, not to the tap target. At 44px tall with 24px of
              // horizontal padding these read as bloated next to the fields above:
              // "All" was 67px wide for ~19px of text, so ~70% of the chip was empty
              // space — MORE box than the search field on LESS type (14px vs 16px),
              // which is what made them look out of proportion.
              //
              // The 44px touch target (Apple HIG minimum) is preserved by the ::after
              // overlay below rather than by inflating the visible pill, so the chip
              // can be chip-sized and still be easy to hit.
              px: '14px',
              py: '8px',
              minHeight: 36,
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 44,
              },
              cursor: 'pointer',
              borderRadius: '999px',
              // Selected fills to the full StatusBadge (its fill + border + text color);
              // unselected is a NEUTRAL grey outline (not status-colored — too busy).
              // Constant 1px border + fixed padding in BOTH states, so toggling never
              // shifts layout. NOTE: the `!important` is required — index.css's
              // `#vera-shadow-root *` border-color rule has ID specificity and
              // otherwise wins, whatever form the colour is written in.
              border: `1px solid ${isOn ? opt.borderColor : border.subtle} !important`,
              backgroundColor: isOn ? opt.fillColor : 'transparent',
              color: isOn ? opt.textColor : 'text.primary',
              transition: 'background-color .12s, border-color .12s, color .12s',
              '&:hover': {
                backgroundColor: isOn ? opt.fillColor : 'action.hover',
              },
            }}
          >
            {/*
              16, matching the fields these chips sit beside.

              The ask was the other way round — take the FIELDS down to the chips'
              14 — but Input.tsx carries the reason not to: 16px is the iOS Safari
              auto-zoom floor, so a focused control under it forces a pinch-zoom on
              tap. That field was 14 once and was raised to 16 by VERA-304, and
              VERA-291 reached the same floor independently for FieldRow/PhoneField.
              Going back down re-introduces a twice-fixed bug on the platform this
              app is actually opened on (a phone, from an NFC tap).

              It is also the minority reading: in the Certificates filter sheet the
              Search input and "Select date" are both 16 and only the chips were 14,
              so the chip was the outlier, not the fields. A chip is not a focusable
              form control, so raising it costs nothing.
            */}
            <Typography sx={{ fontSize: 16, fontWeight: 500, lineHeight: 1, color: 'inherit' }}>
              {opt.label}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default StatusPillFilter;
